import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useOutsideClick } from "@/hooks/use-outside-click";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  trackBeratungsterminClick,
  trackBeratungsterminStart,
} from "@/lib/meta/pixel-tracker";
// Define appointment options with their icons, titles and links
const appointmentOptions = [
  {
    id: "calendar",
    title: "Bewertung Ihrer Immobilie",
    icon: "/Icons/landingpage-bewertung.svg",
    link: "https://crm.propstack.de/public/bc/zgE6PyY3EqwLbYdrdZDBYWTo",
  },
  {
    id: "video",
    title: "Verkauf Ihrer Immobilie",
    icon: "/Icons/verkaufen.svg",
    link: "https://crm.propstack.de/public/bc/B74rKq1aLiUzsWhAZhcxaNLQ",
  },
  {
    id: "phone",
    title: "Kauf einer Immobilie",
    icon: "/Icons/beratung-vorteile-3.svg",
    link: "https://crm.propstack.de/public/bc/tHmR6Kz38dGHig2xkcrJv79a",
  },
  {
    id: "office",
    title: "Vermietung Ihrer Immobilie",
    icon: "/Icons/header-vermieten.svg",
    link: "https://crm.propstack.de/public/bc/xBc3eqdGV5JvBKnNGVXen71Y",
  },
  {
    id: "office",
    title: "Allgemeine Fragen",
    icon: "/Icons/allgemeine.svg",
    link: "https://crm.propstack.de/public/bc/FmHGxUJN6oMHnt3H95MohDAG",
  },
];

export default function BeratungsterminModal({ trigger }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const iframeRef = useRef(null);

  const modalContentRef = useRef(null);
  useOutsideClick(modalContentRef, () => {
    if (isOpen) {
      if (selectedOption) {
        // If in iframe view, go back to options
        setSelectedOption(null);
      } else {
        // If in options view, close modal
        setIsOpen(false);
      }
    }
  });

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    trackBeratungsterminClick(option.link, option.title);
  };

  const handleBack = () => {
    setSelectedOption(null);
  };

  // Correct handler for Dialog's onOpenChange
  const handleOpenChange = (open) => {
    setIsOpen(open); // Update state based on Dialog's request
    if (open) {
      // Track event only when opening
      trackBeratungsterminStart(window.location.href);
    } else {
      // Reset view if closing while iframe is visible
      setSelectedOption(null);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
      },
    }),
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent
        className="sm:max-w-[600px] p-0 overflow-hidden"
        ref={modalContentRef}
      >
        <AnimatePresence mode="wait">
          {!selectedOption ? (
            <ScrollArea className="h-[40rem] md:max-h-[50rem]">
              <motion.div
                key="options"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="p-6 py-10"
              >
                <DialogHeader className="pb-5">
                  <DialogTitle className="text-2xl font-serif font-medium ">
                    Zu welchem Thema möchten Sie beraten werden?
                  </DialogTitle>
                  <DialogDescription className="text-base">
                    Es handelt sich um eine telefonische Erstberatung die circa
                    15 Minuten dauert.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid md:grid-cols-2 h-[30rem] md:h-auto gap-4">
                  {appointmentOptions.map((option, i) => (
                    <motion.div
                      key={option.id}
                      custom={i}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <Button
                        variant="outline"
                        className="w-full h-24 flex flex-col items-center justify-center font-normal gap-2 text-base hover:bg-gray-50"
                        onClick={() => handleOptionSelect(option)}
                      >
                        <img
                          src={option.icon}
                          alt={option.title}
                          className="w-8 h-8"
                          onError={(e) => {
                            e.target.src = "/icons/default.svg";
                          }}
                        />
                        <span>{option.title}</span>
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </ScrollArea>
          ) : (
            <motion.div
              key="iframe"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="relative h-[40rem] md:h-[50rem]"
            >
              <Button
                variant="ghost"
                className="absolute left-4 top-4 z-10"
                onClick={handleBack}
              >
                ← Zurück
              </Button>

              <iframe
                ref={iframeRef}
                src={selectedOption.link}
                title={selectedOption.title}
                className="w-full h-full border-0"
                allow="camera; microphone; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                loading="lazy"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
